'use client';

import { ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { KeyboardArrowDown, KeyboardArrowUp } from '@lichtblick/icons';
import { Colors, TextVariants } from '@lichtblick/theme';

import {
  AutocompleteButton,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteOption,
  AutocompleteOptions,
  AutocompleteTrigger,
  Container,
  OptionsContainer,
  CustomCombobox as Combobox,
} from './Autocomplete.styles';

import { Text } from '../Text';

import type { Option } from '@lichtblick/types';

type AutocompleteProps<TValue = string | number | undefined> = {
  defaultValue?: Option<TValue>;
  error?: string | null;
  getOptionKey?: (option: Option<TValue>, index: number) => string;
  isDisabled?: boolean;
  isSmall?: boolean;
  onSelect?: (value?: TValue) => void;
  options: Array<Option<TValue>>;
  placeholder?: string;
  renderItem?: (option: Option<TValue>) => ReactNode;
  setQuery: Dispatch<SetStateAction<string>>;
  value?: TValue;
};

const defaultOption = <TValue,>(): Option<TValue> => ({
  label: '',
  value: undefined,
});

export const Autocomplete = <TValue,>({
  defaultValue,
  error,
  getOptionKey,
  isDisabled,
  isSmall,
  onSelect,
  options,
  placeholder = 'Select',
  renderItem,
  setQuery,
  value,
}: AutocompleteProps<TValue>) => {
  const ButtonRef = useRef<HTMLButtonElement>(null);
  const [isModifierToggledOn, setIsModifierToggledOn] = useState(false);

  const realDefaultValue = defaultValue ?? defaultOption<TValue>();

  const getOptionLabel = useCallback((option: Option<TValue>): string => option.label ?? '', []);

  const getOptionValue = useCallback(
    (option: Option<TValue>): TValue | undefined => option.value as TValue | undefined,
    [],
  );

  const getOptionDeleted = useCallback((option: Option<TValue>): boolean => Boolean(option.isDeleted), []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isComboboxActive = document.activeElement?.getAttribute('role') === 'combobox';

      if (event.altKey && isComboboxActive) {
        setIsModifierToggledOn((previousValue) => !previousValue);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const matchesOptionValue = (optionValue: unknown, selectedValue: unknown) => {
    if (typeof selectedValue === 'object' && selectedValue !== null) {
      return optionValue === selectedValue;
    }

    return optionValue?.toString() === selectedValue?.toString();
  };

  const selectedOption = value ? options.find((opt) => matchesOptionValue(getOptionValue(opt), value)) : undefined;

  const selectedValue = selectedOption ?? realDefaultValue;

  const sortedOptions = useMemo(() => {
    const normalizedOptions = [...options];
    const shouldRevealDeleted = isModifierToggledOn;

    return normalizedOptions
      .filter((option) => shouldRevealDeleted || !getOptionDeleted(option))
      .sort((left, right) => {
        if (shouldRevealDeleted) {
          return 0;
        }

        const leftDeleted = getOptionDeleted(left);
        const rightDeleted = getOptionDeleted(right);

        if (leftDeleted === rightDeleted) {
          return 0;
        }

        return leftDeleted ? 1 : -1;
      });
  }, [getOptionDeleted, isModifierToggledOn, options]);

  const resolveOptionKey = (option: Option<TValue>, index: number) => {
    if (getOptionKey) {
      return getOptionKey(option, index);
    }

    const optionValue = getOptionValue(option);
    const valueKey =
      optionValue === undefined || optionValue === null
        ? `undefined-${index}`
        : typeof optionValue === 'string' || typeof optionValue === 'number' || typeof optionValue === 'boolean'
          ? String(optionValue)
          : JSON.stringify(optionValue);

    return `${getOptionLabel(option) ?? 'option'}-${valueKey}-${index}`;
  };

  return (
    <>
      <Combobox
        $hasError={!!error && error !== ''}
        disabled={isDisabled}
        onChange={(option: unknown) => {
          const selectedOption = option as Option<TValue> | null;

          if (selectedOption) {
            const selectedOptionValue = getOptionValue(selectedOption);
            const currentSelectedValue = getOptionValue(selectedValue);

            if (matchesOptionValue(selectedOptionValue, currentSelectedValue)) {
              onSelect?.(realDefaultValue.value as TValue | undefined);
            } else {
              onSelect?.(selectedOptionValue as TValue | undefined);
            }
          } else {
            onSelect?.(realDefaultValue.value as TValue | undefined);
          }
        }}
        suppressHydrationWarning
        value={selectedValue}
      >
        {({ open: isOpen }) => (
          <Container suppressHydrationWarning>
            <AutocompleteTrigger $isSmall={!!isSmall}>
              <ComboboxInput
                as={AutocompleteInput}
                displayValue={(option: Option<TValue>) => getOptionLabel(option)}
                onBlur={() => setTimeout(() => setQuery(''), 500)}
                onChange={(event) => setQuery(event.target.value)}
                onClick={() => !isOpen && ButtonRef.current?.click()}
                placeholder={placeholder}
              />
              <ComboboxButton as={AutocompleteButton} ref={ButtonRef}>
                {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </ComboboxButton>
            </AutocompleteTrigger>
            <ComboboxOptions as={AutocompleteOptions}>
              <OptionsContainer>
                {sortedOptions?.map?.((option, index) => {
                  const isDeleted = getOptionDeleted(option);

                  return (
                    <ComboboxOption as={AutocompleteOption} key={resolveOptionKey(option, index)} value={option}>
                      <AutocompleteItem
                        $isDeleted={isDeleted}
                        data-deleted={isDeleted}
                        style={{
                          color: isDeleted ? 'var(--colors-gray-50)' : undefined,
                          opacity: isDeleted ? 0.6 : 1,
                          textDecoration: isDeleted ? 'line-through' : 'none',
                        }}
                      >
                        {renderItem ? (
                          renderItem(option)
                        ) : (
                          <>
                            {option.icon}
                            {getOptionLabel(option)}
                          </>
                        )}
                      </AutocompleteItem>
                    </ComboboxOption>
                  );
                })}
              </OptionsContainer>
            </ComboboxOptions>
          </Container>
        )}
      </Combobox>
      {error && error !== '' ? (
        <Text $variant={TextVariants.body.s} color={Colors.red[100]}>
          {error}
        </Text>
      ) : null}
    </>
  );
};
